import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { appAlert, appConfirm } from '../../lib/dialogs';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';

@withText(props => ({
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
}))
@observer
class CalendarTemplateEdit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('calendarTemplate'),
        };
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id } = props;
        const { calendarTemplateStore } = this.props.stores;
        if (emptyFirst) {
            calendarTemplateStore.updateKeyValue('calendarTemplate', {});
        }
        if (id) {
            await calendarTemplateStore.load(id);
        }
    }

    createCalendarTemplate = async () => {
        const { callback = () => {} } = this.props;
        const { calendarTemplateStore } = this.props.stores;
        const { newCalendarTemplate } = calendarTemplateStore;

        if (!newCalendarTemplate.name || newCalendarTemplate.name.trim() === '') {
            await appAlert('Name is required');
            return;
        }

        const response = await calendarTemplateStore.insert(newCalendarTemplate);
        const calendarTemplate = response?.data?.calendarTemplate;

        calendarTemplateStore.updateKeyValue('newCalendarTemplate', {});
        callback(calendarTemplate);
    }

    saveCalendarTemplate = async () => {
        const { callback = () => {} } = this.props;
        const { calendarTemplateStore } = this.props.stores;
        const { calendarTemplate } = calendarTemplateStore;

        if (!calendarTemplate.name || calendarTemplate.name.trim() === '') {
            await appAlert('Name is required');
            return;
        }

        await calendarTemplateStore.save(calendarTemplate, calendarTemplate.id);
        callback(calendarTemplate);
    }

    deleteCalendarTemplate = async () => {
        if (this.deletionPending) return;
        const { callback = () => {}, id: editorId } = this.props;
        const { calendarTemplateStore, userStore } = this.props.stores;
        const templateId = calendarTemplateStore.calendarTemplate?.id;
        if (!templateId) return;
        const userId = userStore.user?.id;
        const customerId = userStore.customer?.id;
        const isCurrent = () => !this.templateUnmounted && this.props.id === editorId
            && calendarTemplateStore.calendarTemplate?.id === templateId
            && userStore.user?.id === userId && userStore.customer?.id === customerId;

        this.deletionPending = true;
        this.setState({ deletingTemplate: true });
        try {
            const approved = await appConfirm('Are you sure you want to delete this template?');
            if (!approved || !isCurrent()) return;
            await calendarTemplateStore.delete(templateId);
            if (!isCurrent()) return;
            calendarTemplateStore.updateKeyValue('calendarTemplate', {});
            callback();
        } finally {
            this.deletionPending = false;
            if (!this.templateUnmounted) this.setState({ deletingTemplate: false });
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillUnmount() {
        this.templateUnmounted = true;
    }

    render() {
        const { isNew, drawerLevel, lookupArray } = this.props;
        const { sections } = this.state;
        const { userStore, calendarTemplateStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin } = userStore;
        const { newCalendarTemplate, calendarTemplate } = calendarTemplateStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const currentTemplate = isNew ? newCalendarTemplate : calendarTemplate;

        return (<>
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        {isNew ? <Text id='calendarTemplate.new'>New Calendar Template</Text> : <Text id='calendarTemplate.edit'>Edit Calendar Template</Text>}
                    </div>

                    {currentTemplate?.backgroundColor && <>
                        <div class='mx-4 mb-3'>
                            <div class='d-flex align-items-center'>
                                <span class='me-2'><Text id='calendarTemplate.preview'>Preview:</Text></span>
                                <div
                                    class='py-2 px-3 rounded-pill'
                                    style={`background-color: ${currentTemplate.backgroundColor}; color: ${currentTemplate.color || '#333'};`}
                                >
                                    <span class='me-1'>{currentTemplate.name || 'Template Name'}</span>
                                    <small class='opacity-75'>({currentTemplate.durationMinutes || 30} min)</small>
                                </div>
                            </div>
                        </div>
                    </>}

                    <div class='w-100 d-flex flex-column justify-content-start'>
                        <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sections}
                                holdingStore={calendarTemplateStore}
                                objectName={isNew ? 'newCalendarTemplate' : 'calendarTemplate'}
                                isNew={isNew}
                                drawerLevel={drawerLevel}
                                isAdmin={false}
                                hideAdmin={true}
                                lookupArray={lookupArray}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class='w-100 d-flex flex-row justify-content-between mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                {!isNew && <>
                    <button
                        type='button'
                        class='btn btn-outline-danger rounded-pill'
                        onClick={this.deleteCalendarTemplate}
                        disabled={!!this.state.deletingTemplate}
                    >
                        <i class='fa-solid fa-trash me-2' />
                        <Text id='calendarTemplate.delete'>Delete</Text>
                    </button>
                </>}
                <div class='flex-fill' />
                <button
                    type='button'
                    class='btn btn-primary rounded-pill'
                    onClick={isNew ? this.createCalendarTemplate : this.saveCalendarTemplate}
                    disabled={!!this.state.deletingTemplate}
                >
                    {isNew ? <Text id='calendarTemplate.create'>Create Template</Text> : <Text id='calendarTemplate.save'>Save Template</Text>}
                </button>
            </div>
        </>);
    }
}

export default CalendarTemplateEdit;
